import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/layouts/MainLayout';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import Badge from '../../../components/common/Badge';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { useToast } from '../../../components/common/Toast';
import { sellerService } from '../../../services/seller.service';
import { theme } from '../../../config/theme';
import { ONBOARDING_STATUS } from '../../../config/constants';
import { CheckCircleIcon } from '../../../components/icons/Icons';

const OnboardingStatus = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await sellerService.getStatus();
      setStatus(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.clear();
        navigate('/login');
      } else if (error.response?.status === 404) {
        toast.error('Seller profile not found. Please register again.');
        localStorage.clear();
        navigate('/register');
      } else {
        toast.error('Failed to load onboarding status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    setSubmitting(true);
    try {
      await sellerService.submitForReview();
      toast.success('Application submitted for review!');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (!status) return null;

  const { seller, verification, documents, bankDetails, canSubmit } = status;

  const steps = [
    { id: 1, label: 'Registration', completed: true },
    { id: 2, label: 'Phone Verified', completed: verification?.phoneVerified },
    { id: 3, label: 'Profile Completed', completed: documents?.allUploaded && bankDetails?.completed },
    { id: 4, label: 'Submitted for Review', completed: seller?.onboarding_status !== ONBOARDING_STATUS.PENDING && seller?.onboarding_status !== ONBOARDING_STATUS.DOCUMENTS_UPLOADED },
  ];

  const getStatusVariant = (status) => {
    const map = { pending: 'warning', documents_uploaded: 'info', under_review: 'info', approved: 'success', rejected: 'error' };
    return map[status] || 'default';
  };

  const stepItemStyles = (completed) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.md,
    backgroundColor: completed ? `${theme.colors.success}10` : theme.colors.gray[50],
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${completed ? theme.colors.success : theme.colors.gray[200]}`,
  });

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '800px', margin: '0 auto' }}>
        <Card padding="xl">
          <div style={{ textAlign: 'center', marginBottom: theme.spacing['2xl'] }}>
            <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.md }}>
              Seller Onboarding Progress
            </h1>
            <Badge variant={getStatusVariant(seller?.onboarding_status)}>
              {seller?.onboarding_status?.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>

          <div style={{ marginBottom: theme.spacing['2xl'] }}>
            {steps.map(step => (
              <div key={step.id} style={stepItemStyles(step.completed)}>
                {step.completed ? (
                  <div style={{ marginRight: theme.spacing.md, color: theme.colors.success }}>
                    <CheckCircleIcon size={24} />
                  </div>
                ) : (
                  <div style={{ 
                    width: '24px', 
                    height: '24px', 
                    marginRight: theme.spacing.md, 
                    border: `2px solid ${theme.colors.gray[300]}`, 
                    borderRadius: '50%' 
                  }} />
                )}
                <span style={{ flex: 1, fontWeight: theme.typography.fontWeight.medium }}>{step.label}</span>
                {step.id === 2 && !step.completed && (
                  <Button size="sm" onClick={() => navigate('/seller/verify-otp')}>Verify Now</Button>
                )}
                {step.id === 3 && !step.completed && (
                  <Button size="sm" onClick={() => navigate('/seller/onboarding/complete-profile')}>Complete Now</Button>
                )}
              </div>
            ))}
          </div>

          {canSubmit && seller?.onboarding_status === ONBOARDING_STATUS.DOCUMENTS_UPLOADED && (
            <div style={{ backgroundColor: theme.colors.gray[50], padding: theme.spacing.lg, borderRadius: theme.borderRadius.md, marginBottom: theme.spacing.lg }}>
              <h3 style={{ marginBottom: theme.spacing.md, fontWeight: theme.typography.fontWeight.semibold }}>Ready to Submit!</h3>
              <p style={{ marginBottom: theme.spacing.lg, color: theme.colors.text.secondary }}>
                Your profile is complete. Submit your application for admin review.
              </p>
              <Button onClick={handleSubmitForReview} loading={submitting} fullWidth>Submit for Review</Button>
            </div>
          )}

          <div style={{ backgroundColor: theme.colors.gray[50], padding: theme.spacing.lg, borderRadius: theme.borderRadius.md }}>
            <h3 style={{ marginBottom: theme.spacing.md, fontWeight: theme.typography.fontWeight.semibold }}>Required Documents:</h3>
            <ul style={{ lineHeight: 1.8, paddingLeft: theme.spacing.lg }}>
              <li>Aadhaar Card (Mandatory)</li>
              <li>PAN Card (Mandatory)</li>
              <li>GST Certificate (Optional)</li>
              <li>Trade License (Optional)</li>
              <li>Shop Photo (Optional)</li>
              <li>Cancelled Cheque (Optional)</li>
            </ul>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default OnboardingStatus;